"use client";

import { type ReactNode, useMemo, useState } from "react";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { tPlural } from "@/lib/i18n/interpolate";

/* ------------------------------------------------------------------ */
/*  Pronoun lists (module level) — first and second person, longest   */
/*  forms first so the alternation prefers "you're" over "you".       */
/* ------------------------------------------------------------------ */

const FIRST_PERSON: string[] = [
  "ourselves",
  "myself",
  "we're",
  "we've",
  "we'll",
  "ours",
  "mine",
  "i'm",
  "i've",
  "i'll",
  "i'd",
  "our",
  "me",
  "my",
  "we",
  "us",
  "i",
];

const SECOND_PERSON: string[] = [
  "yourselves",
  "yourself",
  "you're",
  "yours",
  "your",
  "you",
];

const FIRST_PERSON_SET = new Set(FIRST_PERSON);
const SECOND_PERSON_SET = new Set(SECOND_PERSON);

const RE_PRONOUN = new RegExp(
  `\\b(?:${[...FIRST_PERSON, ...SECOND_PERSON]
    .sort((a, b) => b.length - a.length)
    .join("|")
    .replace(/'/g, "['’]")})\\b`,
  "gi"
);
const RE_SPLIT_WORDS = /\s+/;
const RE_CURLY_APOSTROPHE = /’/g;
/** "us" and "me" double as the abbreviations US and ME (Maine), so all-caps is not a pronoun. */
const CASE_SENSITIVE_LOOKALIKES = new Set(["us", "me"]);

/** Every pronoun match in the text, skipping the all-caps country and state codes. */
function findPronouns(text: string): RegExpExecArray[] {
  return Array.from(text.matchAll(RE_PRONOUN)).filter((match) => {
    const isAllCaps = match[0] === match[0].toUpperCase();
    return !(
      isAllCaps && CASE_SENSITIVE_LOOKALIKES.has(match[0].toLowerCase())
    );
  });
}

/* ------------------------------------------------------------------ */
/*  Analysis                                                          */
/* ------------------------------------------------------------------ */

interface PronounCount {
  pronoun: string;
  count: number;
}

interface AnalysisResult {
  wordCount: number;
  firstTotal: number;
  secondTotal: number;
  density: number;
  firstCounts: PronounCount[];
  secondCounts: PronounCount[];
  highlighted: ReactNode[];
}

function normalizePronoun(raw: string): string {
  return raw.toLowerCase().replace(RE_CURLY_APOSTROPHE, "'");
}

function buildHighlighted(text: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  let last = 0;
  for (const match of findPronouns(text)) {
    if (match.index > last) {
      nodes.push(text.slice(last, match.index));
    }
    const isSecond = SECOND_PERSON_SET.has(normalizePronoun(match[0]));
    const markClass = isSecond
      ? "rounded bg-blue-200 px-0.5 dark:bg-blue-900/60 dark:text-blue-100"
      : "rounded bg-yellow-200 px-0.5 dark:bg-yellow-900/60 dark:text-yellow-100";
    nodes.push(
      <mark className={markClass} key={`pronoun-${match.index}`}>
        {match[0]}
      </mark>
    );
    last = match.index + match[0].length;
  }
  if (last < text.length) {
    nodes.push(text.slice(last));
  }
  return nodes;
}

function toSortedCounts(counts: Map<string, number>): PronounCount[] {
  return Array.from(counts.entries())
    .map(([pronoun, count]) => ({ pronoun, count }))
    .sort((a, b) => b.count - a.count);
}

export function analyzePronouns(text: string): AnalysisResult | null {
  const trimmed = text.trim();
  if (trimmed.length === 0) {
    return null;
  }

  const firstMap = new Map<string, number>();
  const secondMap = new Map<string, number>();
  for (const match of findPronouns(text)) {
    const key = normalizePronoun(match[0]);
    if (FIRST_PERSON_SET.has(key)) {
      firstMap.set(key, (firstMap.get(key) ?? 0) + 1);
    } else if (SECOND_PERSON_SET.has(key)) {
      secondMap.set(key, (secondMap.get(key) ?? 0) + 1);
    }
  }

  const firstTotal = Array.from(firstMap.values()).reduce((s, n) => s + n, 0);
  const secondTotal = Array.from(secondMap.values()).reduce((s, n) => s + n, 0);
  const wordCount = trimmed.split(RE_SPLIT_WORDS).filter(Boolean).length;
  const density =
    wordCount > 0 ? ((firstTotal + secondTotal) / wordCount) * 100 : 0;

  return {
    wordCount,
    firstTotal,
    secondTotal,
    density,
    firstCounts: toSortedCounts(firstMap),
    secondCounts: toSortedCounts(secondMap),
    highlighted: buildHighlighted(text),
  };
}

/* ------------------------------------------------------------------ */
/*  Stat card + count list                                            */
/* ------------------------------------------------------------------ */

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4 text-center">
      <p className="font-semibold text-2xl text-foreground">{value}</p>
      <p className="mt-1 text-muted-foreground text-sm">{label}</p>
    </div>
  );
}

function PronounCountList({
  title,
  counts,
  occurrenceCount,
  locale,
}: {
  title: string;
  counts: PronounCount[];
  occurrenceCount: string;
  locale: string;
}) {
  return (
    <section className="space-y-3">
      <h2 className="font-semibold text-foreground text-lg">{title}</h2>
      <div className="space-y-3">
        {counts.map((item) => (
          <div
            className="flex items-center justify-between rounded-lg border border-border bg-card p-4"
            key={item.pronoun}
          >
            <span className="font-medium text-foreground text-sm">
              {item.pronoun}
            </span>
            <span className="text-muted-foreground text-sm">
              {tPlural(occurrenceCount, locale, item.count)}
            </span>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  Page Component                                                    */
/* ------------------------------------------------------------------ */

/** English strings. A translated content file overrides these through `ui`. */
export const FIRST_PERSON_CHECKER_STRINGS = {
  occurrenceCount: "one: {count} occurrence|other: {count} occurrences",
  textToCheckForFirst: "Text to check for first- and second-person pronouns",
  pasteOrTypeYourText:
    "Paste or type your text here to find first- and second-person pronouns...",
  totalWords: "Total Words",
  firstPerson: "First Person",
  secondPerson: "Second Person",
  per100Words: "Per 100 Words",
  yourTextWithPronounsHighlighted: "Your Text with Pronouns Highlighted",
  firstPersonIsHighlightedIn:
    "First person is highlighted in yellow, second person in blue.",
  firstPersonPronouns: "First-Person Pronouns",
  secondPersonPronouns: "Second-Person Pronouns",
  noFirstOrSecondPerson: "No first- or second-person pronouns found",
  yourTextIsEntirelyIn: "Your text is entirely in the third person.",
  shouldYouAvoidFirstPerson: "Should you avoid first person?",
  itDependsOnYourStyle:
    'It depends on your style guide, not on a blanket rule. Many fields now allow or encourage first person — APA 7 explicitly recommends using "I" for single-author papers rather than "the author". Other disciplines, journals, and instructors still expect third person. Second person ("you", "your") is different: most academic prose avoids addressing the reader directly regardless of field. Check what your target venue expects, then decide each highlighted occurrence deliberately.',
} as const;

export default function FirstPersonCheckerWidget() {
  const [text, setText] = useState("");

  const analysis = useMemo(() => analyzePronouns(text), [text]);

  const strings = useToolUi(FIRST_PERSON_CHECKER_STRINGS);
  const fmt = useToolNumber();
  const locale = useToolLocale();

  return (
    <>
      {/* Input */}
      <textarea
        aria-label={strings.textToCheckForFirst}
        className="min-h-[200px] w-full resize-y rounded-lg border border-border bg-card p-4 text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
        onChange={(e) => setText(e.target.value)}
        placeholder={strings.pasteOrTypeYourText}
        value={text}
      />
      {/* Stats */}
      {analysis && (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <StatCard
            label={strings.totalWords}
            value={fmt(analysis.wordCount)}
          />
          <StatCard
            label={strings.firstPerson}
            value={fmt(analysis.firstTotal)}
          />
          <StatCard
            label={strings.secondPerson}
            value={fmt(analysis.secondTotal)}
          />
          <StatCard
            label={strings.per100Words}
            value={fmt(analysis.density, {
              minimumFractionDigits: 1,
              maximumFractionDigits: 1,
            })}
          />
        </div>
      )}
      {/* Highlighted text */}
      {analysis && analysis.firstTotal + analysis.secondTotal > 0 && (
        <section className="space-y-3">
          <h2 className="font-semibold text-foreground text-lg">
            {strings.yourTextWithPronounsHighlighted}
          </h2>
          <p className="text-muted-foreground text-sm leading-relaxed">
            {strings.firstPersonIsHighlightedIn}
          </p>
          <div className="whitespace-pre-wrap rounded-lg border border-border bg-card p-4 text-foreground text-sm leading-relaxed">
            {analysis.highlighted}
          </div>
        </section>
      )}
      {/* Per-pronoun counts */}
      {analysis && analysis.firstCounts.length > 0 && (
        <PronounCountList
          counts={analysis.firstCounts}
          locale={locale}
          occurrenceCount={strings.occurrenceCount}
          title={strings.firstPersonPronouns}
        />
      )}
      {analysis && analysis.secondCounts.length > 0 && (
        <PronounCountList
          counts={analysis.secondCounts}
          locale={locale}
          occurrenceCount={strings.occurrenceCount}
          title={strings.secondPersonPronouns}
        />
      )}
      {/* None found */}
      {analysis && analysis.firstTotal + analysis.secondTotal === 0 && (
        <div className="rounded-lg border border-border bg-card p-5 text-center">
          <p className="font-medium text-foreground">
            {strings.noFirstOrSecondPerson}
          </p>
          <p className="mt-1 text-muted-foreground text-sm">
            {strings.yourTextIsEntirelyIn}
          </p>
        </div>
      )}
      {/* Explainer */}
      <section className="space-y-3 rounded-lg border border-border bg-card p-5">
        <h2 className="font-semibold text-foreground text-lg">
          {strings.shouldYouAvoidFirstPerson}
        </h2>
        <p className="text-muted-foreground text-sm leading-relaxed">
          {strings.itDependsOnYourStyle}
        </p>
      </section>
    </>
  );
}
